import { useEffect, useState } from "react";
import { Plus, Bot, Edit, TestTube, Trash2, CheckCircle2, Clock, Package, User, MessageSquare, AlertTriangle, Copy, MessageCircle, Instagram } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AgentDialog } from "@/components/agents/AgentDialog";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { PaywallOverlay } from "@/components/layout/PaywallOverlay";
import { useResourceLimits } from "@/hooks/useResourceLimits";
import { whatsappIntegrationDisplayLabel } from "@/lib/whatsapp-integration-label";
import { fetchAllPages, insertInBatches } from "@/lib/supabase-pagination";

interface BusinessHours {
  day_of_week: number;
  start_time: string;
  end_time: string;
}
interface AgentProductSummary {
  count: number;
  sampleNames: string[];
}
interface Agent {
  id: string;
  name: string;
  role: string;
  tone: string;
  prompt_template?: string;
  is_active: boolean;
  productSummary?: AgentProductSummary;
  businessHours?: BusinessHours[];
  display_name?: string;
  language?: string;
  timezone?: string;
}
const roleLabels: Record<string, string> = {
  vendas: "Vendas",
  suporte: "Suporte",
  sdr: "SDR",
  agendamentos: "Agendamentos"
};
export default function Agents() {
  const {
    user
  } = useAuth();
  const {
    workspaceId
  } = useWorkspace(user?.id);
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const {
    shouldShowPaywall
  } = useSubscription();
  const { checkCanCreate } = useResourceLimits(workspaceId);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [agentToDuplicate, setAgentToDuplicate] = useState<Agent | null>(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  // Toggle scope dialog state
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [agentToToggle, setAgentToToggle] = useState<Agent | null>(null);
  const [pendingNewStatus, setPendingNewStatus] = useState<boolean>(false);
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<string[]>([]);
  const [agentIntegrationLinks, setAgentIntegrationLinks] = useState<AgentIntegrationLink[]>([]);
  const [affectedCount, setAffectedCount] = useState<number | null>(null);
  const [blockedCount, setBlockedCount] = useState<number>(0);
  const [isApplyingToggle, setIsApplyingToggle] = useState(false);
  const [hasMultipleActiveAgents, setHasMultipleActiveAgents] = useState(false);
  useEffect(() => {
    if (workspaceId) {
      fetchAgents();
    }
  }, [workspaceId]);
  const fetchAgents = async (): Promise<Agent[]> => {
    try {
      const {
        data,
        error
      } = await supabase.from("agents").select("*").eq("workspace_id", workspaceId)
        .order("is_active", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) {
        console.error("Error fetching agents:", error);
        throw error;
      }

      const agentIds = data?.map((a) => a.id) || [];
      const productSummaryByAgent = new Map<string, AgentProductSummary>();

      if (agentIds.length > 0) {
        const allLinks = await fetchAllPages<{
          agent_id: string;
          products: { name: string } | null;
        }>((from, to) =>
          supabase
            .from("agent_products")
            .select("agent_id, products(name)")
            .in("agent_id", agentIds)
            .range(from, to),
        );

        for (const link of allLinks) {
          const current = productSummaryByAgent.get(link.agent_id) ?? {
            count: 0,
            sampleNames: [],
          };
          current.count += 1;
          const name = link.products?.name;
          if (name && current.sampleNames.length < 3) {
            current.sampleNames.push(name);
          }
          productSummaryByAgent.set(link.agent_id, current);
        }
      }

      // Fetch business hours for all agents
      const {
        data: allBusinessHours
      } = await supabase.from("agent_business_hours").select("*").in("agent_id", agentIds).eq("is_enabled", true).order("day_of_week");
      const agentsWithHours = (data?.map((agent) => {
        const hours = allBusinessHours?.filter((h) => h.agent_id === agent.id) || [];
        return {
          ...agent,
          productSummary: productSummaryByAgent.get(agent.id),
          businessHours: hours,
        };
      }) || []).sort((a, b) => {
        const aOn = a.is_active !== false;
        const bOn = b.is_active !== false;
        if (aOn !== bOn) return aOn ? -1 : 1;
        return 0;
      });
      setAgents(agentsWithHours);
      return agentsWithHours;
    } catch (error: any) {
      toast({
        title: "Erro ao carregar agentes",
        description: error.message || "Não foi possível carregar os agentes.",
        variant: "destructive"
      });
      return [];
    }
  };
  const handleDeleteClick = (agent: Agent) => {
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };
  const handleDeleteConfirm = async () => {
    if (!agentToDelete) return;
    try {
      const {
        error
      } = await supabase.from("agents").delete().eq("id", agentToDelete.id);
      if (error) throw error;
      toast({
        title: "Agente excluído",
        description: "O agente foi removido com sucesso."
      });
      fetchAgents();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir agente",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
    }
  };
  const handleCreateAgent = async () => {
    const check = await checkCanCreate('agents');
    if (!check.can_create) {
      toast({
        title: "Limite de agentes atingido",
        description: `Seu plano (${check.plan}) permite até ${check.limit} agente(s). Você já possui ${check.current}. Faça upgrade para criar mais.`,
        variant: "destructive"
      });
      return;
    }
    setSelectedAgent(null);
    setIsDialogOpen(true);
  };
  const handleEditAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setIsDialogOpen(true);
  };

  const handleDuplicateClick = (agent: Agent) => {
    setAgentToDuplicate(agent);
    setDuplicateName(`Cópia de ${agent.name}`);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!agentToDuplicate || !workspaceId || duplicateName.trim().length < 2) return;
    setIsDuplicating(true);
    try {
      const check = await checkCanCreate('agents');
      if (!check.can_create) {
        toast({
          title: "Limite de agentes atingido",
          description: `Seu plano (${check.plan}) permite até ${check.limit} agente(s). Faça upgrade para criar mais.`,
          variant: "destructive"
        });
        return;
      }

      // Fetch full agent row
      const { data: original, error: fetchErr } = await supabase
        .from("agents")
        .select("*")
        .eq("id", agentToDuplicate.id)
        .single();
      if (fetchErr || !original) throw fetchErr || new Error("Agente não encontrado");

      // Build new agent (remove system fields)
      const { id, created_at, updated_at, ...agentFields } = original;
      const { data: newAgent, error: insertErr } = await supabase
        .from("agents")
        .insert({ ...agentFields, name: duplicateName.trim(), is_active: false } as any)
        .select("id")
        .single();
      if (insertErr || !newAgent) throw insertErr || new Error("Erro ao criar agente");

      // Copy related tables in parallel
      const [productsRes, hoursRes, followupRes] = await Promise.all([
        fetchAllPages<{ product_id: string }>((from, to) =>
          supabase
            .from("agent_products")
            .select("product_id")
            .eq("agent_id", agentToDuplicate.id)
            .range(from, to),
        ),
        supabase.from("agent_business_hours").select("day_of_week, start_time, end_time, is_enabled, break_start, break_end").eq("agent_id", agentToDuplicate.id),
        supabase.from("agent_followup_config").select("enabled, interval_minutes, max_attempts").eq("agent_id", agentToDuplicate.id)
      ]);

      const copyOps: PromiseLike<any>[] = [];
      if (productsRes.length) {
        copyOps.push(
          insertInBatches(
            supabase.from("agent_products"),
            productsRes.map((p) => ({ agent_id: newAgent.id, product_id: p.product_id })),
          ),
        );
      }
      if (hoursRes.data?.length) {
        copyOps.push(supabase.from("agent_business_hours").insert(
          hoursRes.data.map(h => ({ agent_id: newAgent.id, day_of_week: h.day_of_week, start_time: h.start_time, end_time: h.end_time, is_enabled: h.is_enabled, break_start: (h as any).break_start ?? null, break_end: (h as any).break_end ?? null }))
        ).then());
      }
      if (followupRes.data?.length) {
        copyOps.push(supabase.from("agent_followup_config").insert(
          followupRes.data.map(f => ({ agent_id: newAgent.id, enabled: f.enabled, interval_minutes: f.interval_minutes, max_attempts: f.max_attempts }))
        ).then());
      }
      await Promise.all(copyOps);

      toast({ title: "Agente duplicado", description: `"${duplicateName.trim()}" criado com sucesso (desativado).` });
      fetchAgents();
    } catch (error: any) {
      toast({ title: "Erro ao duplicar agente", description: error.message, variant: "destructive" });
    } finally {
      setIsDuplicating(false);
      setDuplicateDialogOpen(false);
      setAgentToDuplicate(null);
    }
  };
  type MessagingChannel = "whatsapp" | "instagram" | "messenger";

  type AgentIntegrationLink = {
    id: string;
    channel: MessagingChannel;
    provider: string;
    instance_identifier: string | null;
    config: Record<string, unknown> | null;
    instagram_account_id: string | null;
    label: string;
    detail?: string;
  };

  type AgentConversationScope = {
    integrationIds: string[];
    channels: MessagingChannel[];
  };

  const NO_MATCH_CONVERSATION_ID = "00000000-0000-0000-0000-000000000000";

  const cfgString = (config: Record<string, unknown> | null, key: string): string => {
    const value = config?.[key];
    return typeof value === "string" ? value : "";
  };

  const formatIntegrationLabel = (
    link: Pick<AgentIntegrationLink, "channel" | "provider" | "instance_identifier" | "config" | "instagram_account_id">,
  ): { label: string; detail?: string } => {
    const config = link.config;
    if (
      link.provider === "whatsapp_evolution" ||
      link.provider === "whatsapp_cloud" ||
      link.provider === "whatsapp_uazapi"
    ) {
      const label = whatsappIntegrationDisplayLabel(
        {
          instance_identifier: link.instance_identifier,
          provider: link.provider,
          config: {
            friendly_name: cfgString(config, "friendly_name") || null,
            instance_name: cfgString(config, "instanceName") || cfgString(config, "instance_name") || null,
            phone_number: cfgString(config, "phone_number") || cfgString(config, "phone") || null,
            display_phone_number: cfgString(config, "display_phone_number") || null,
            verified_name: cfgString(config, "verified_name") || null,
          },
        },
        link.provider === "whatsapp_cloud"
          ? "WhatsApp API Oficial"
          : link.provider === "whatsapp_uazapi"
            ? "WhatsApp Business"
            : "WhatsApp",
      );
      const detail = link.provider === "whatsapp_cloud"
        ? (cfgString(config, "display_phone_number") || cfgString(config, "verified_name") || undefined)
        : (cfgString(config, "phone_number") || cfgString(config, "phone") || cfgString(config, "number") || undefined);
      return { label, detail };
    }
    if (link.provider === "instagram") {
      const username = cfgString(config, "instagram_username");
      const label = username ? `@${username}` : cfgString(config, "page_name") || "Instagram";
      const detail = cfgString(config, "instagram_name") || undefined;
      return { label, detail };
    }
    if (link.provider === "messenger") {
      const label = cfgString(config, "page_name") || "Messenger";
      return { label };
    }
    return { label: link.instance_identifier || "Integração" };
  };

  const providerToChannel = (provider: string): MessagingChannel | null => {
    if (
      provider === "whatsapp_evolution" ||
      provider === "whatsapp_cloud" ||
      provider === "whatsapp_uazapi"
    )
      return "whatsapp";
    if (provider === "instagram") return "instagram";
    if (provider === "messenger") return "messenger";
    return null;
  };

  /** Integrações ativas onde este agente é o assigned_agent_id (DM/conversas). */
  const fetchAgentIntegrationLinks = async (agentId: string): Promise<AgentIntegrationLink[]> => {
    if (!workspaceId) return [];
    const { data } = await supabase
      .from("integrations")
      .select("id, provider, status, instance_identifier, config, instagram_account_id")
      .eq("workspace_id", workspaceId)
      .eq("assigned_agent_id", agentId)
      .eq("status", "active");
    const links: AgentIntegrationLink[] = [];
    (data || []).forEach((row) => {
      const channel = providerToChannel(row.provider);
      if (!channel) return;
      const partial = {
        channel,
        provider: row.provider,
        instance_identifier: row.instance_identifier,
        config: (row.config as Record<string, unknown> | null) ?? null,
        instagram_account_id: row.instagram_account_id,
      };
      const { label, detail } = formatIntegrationLabel(partial);
      links.push({
        id: row.id,
        ...partial,
        label,
        detail,
      });
    });
    return links;
  };

  const buildConversationScope = (
    links: AgentIntegrationLink[],
    integrationIds: string[],
  ): AgentConversationScope => {
    const filtered = links.filter((link) => integrationIds.includes(link.id));
    return {
      integrationIds: filtered.map((link) => link.id),
      channels: [...new Set(filtered.map((link) => link.channel))],
    };
  };

  const applyAgentConversationScope = <T extends { eq: (col: string, val: string) => T; in: (col: string, vals: string[]) => T; or: (filter: string) => T }>(
    query: T,
    agentId: string,
    scope: AgentConversationScope,
  ): T => {
    if (scope.integrationIds.length === 0) {
      return query.eq("id", NO_MATCH_CONVERSATION_ID);
    }
    const integrationFilter = scope.integrationIds.join(",");
    if (scope.channels.length === 0) {
      return query.in("integration_id", scope.integrationIds);
    }
    const channelFilter = scope.channels.join(",");
    // Escopo pela integração (fonte de verdade); legado sem integration_id usa channel + agent_id.
    return query.or(
      `integration_id.in.(${integrationFilter}),and(integration_id.is.null,channel.in.(${channelFilter}),agent_id.eq.${agentId})`,
    );
  };

  const conversationBlocksAgentActivation = async (conversationId: string): Promise<boolean> => {
    const { data, error } = await supabase.rpc("conversation_lead_blocks_agent_activation", {
      p_conversation_id: conversationId,
    });
    if (error) {
      console.error("conversation_lead_blocks_agent_activation:", error);
      return false;
    }
    return data === true;
  };

  const filterConversationsByAgentStageBlock = async (
    conversations: Array<{ id: string; lead_id: string | null }>,
    shouldBlock: boolean,
  ): Promise<string[]> => {
    const results = await Promise.all(
      conversations.map(async (conversation) => {
        if (!conversation.lead_id) return { id: conversation.id, blocked: false };
        const blocked = await conversationBlocksAgentActivation(conversation.id);
        return { id: conversation.id, blocked };
      }),
    );
    return results
      .filter((row) => (shouldBlock ? row.blocked : !row.blocked))
      .map((row) => row.id);
  };

  const fetchScopedOpenConversations = async (
    agentId: string,
    state: "active" | "paused",
    scope: AgentConversationScope,
  ): Promise<Array<{ id: string; lead_id: string | null }>> => {
    if (!workspaceId || scope.integrationIds.length === 0) return [];

    let q: any = supabase
      .from("conversations")
      .select("id, lead_id")
      .eq("workspace_id", workspaceId)
      .eq("is_closed", false)
      .eq("state", state);
    q = applyAgentConversationScope(q, agentId, scope);

    const { data, error } = await q;
    if (error) {
      console.error("fetchScopedOpenConversations error:", error);
      return [];
    }

    return data || [];
  };

  const fetchEligibleConversationIds = async (
    agentId: string,
    newStatus: boolean,
    scope: AgentConversationScope,
  ): Promise<string[]> => {
    const conversations = await fetchScopedOpenConversations(
      agentId,
      newStatus ? "paused" : "active",
      scope,
    );
    if (!newStatus) return conversations.map((c) => c.id);
    return filterConversationsByAgentStageBlock(conversations, false);
  };

  const fetchActiveBlockedConversationIds = async (
    agentId: string,
    scope: AgentConversationScope,
  ): Promise<string[]> => {
    const conversations = await fetchScopedOpenConversations(agentId, "active", scope);
    return filterConversationsByAgentStageBlock(conversations, true);
  };

  const fetchAffectedCount = async (
    agentId: string,
    newStatus: boolean,
    scope: AgentConversationScope,
  ): Promise<number> => {
    const ids = await fetchEligibleConversationIds(agentId, newStatus, scope);
    return ids.length;
  };

  const fetchBlockedActivationCount = async (
    agentId: string,
    scope: AgentConversationScope,
  ): Promise<number> => {
    const paused = await fetchScopedOpenConversations(agentId, "paused", scope);
    const blocked = await filterConversationsByAgentStageBlock(paused, true);
    return blocked.length;
  };

  const handleQuickToggle = async (agent: Agent) => {
    const newStatus = !agent.is_active;
    setAgentToToggle(agent);
    setPendingNewStatus(newStatus);
    setAffectedCount(null);
    setBlockedCount(0);
    setIsApplyingToggle(false);

    const links = await fetchAgentIntegrationLinks(agent.id);
    const integrationIds = links.map((link) => link.id);
    setAgentIntegrationLinks(links);
    setSelectedIntegrationIds(integrationIds);

    const activeAgentsCount = agents.filter(a => a.is_active).length;
    setHasMultipleActiveAgents(activeAgentsCount > 1);

    const scope = buildConversationScope(links, integrationIds);
    const count = await fetchAffectedCount(agent.id, newStatus, scope);
    setAffectedCount(count);
    if (newStatus) {
      const blocked = await fetchBlockedActivationCount(agent.id, scope);
      setBlockedCount(blocked);
    }

    setToggleDialogOpen(true);
  };

  // Recompute count when integration selection changes
  useEffect(() => {
    if (!toggleDialogOpen || !agentToToggle) return;
    let cancelled = false;
    const scope = buildConversationScope(agentIntegrationLinks, selectedIntegrationIds);
    fetchAffectedCount(agentToToggle.id, pendingNewStatus, scope).then(c => {
      if (!cancelled) setAffectedCount(c);
    });
    if (pendingNewStatus) {
      fetchBlockedActivationCount(agentToToggle.id, scope).then(b => {
        if (!cancelled) setBlockedCount(b);
      });
    } else if (!cancelled) {
      setBlockedCount(0);
    }
    return () => { cancelled = true; };
  }, [selectedIntegrationIds, toggleDialogOpen, pendingNewStatus, agentIntegrationLinks]);

  const updateAgentOnly = async (
    agent: Agent,
    newStatus: boolean,
    options?: { skipConversationSideEffects?: boolean },
  ) => {
    const { error } = await supabase.rpc("set_agent_active_status", {
      p_agent_id: agent.id,
      p_is_active: newStatus,
      p_skip_conversation_side_effects: options?.skipConversationSideEffects ?? false,
    });
    if (error) throw error;
    setAgents(prev => prev.map(a => a.id === agent.id ? { ...a, is_active: newStatus } : a));
  };

  const handleToggleAgentOnly = async () => {
    if (!agentToToggle) return;
    setIsApplyingToggle(true);
    try {
      await updateAgentOnly(agentToToggle, pendingNewStatus, {
        skipConversationSideEffects: true,
      });
      toast({
        title: pendingNewStatus ? "Agente ativado" : "Agente desativado",
        description: pendingNewStatus
          ? "O agente está pronto para atender."
          : "O agente não responderá mais às novas conversas. Conversas existentes não foram alteradas.",
      });
      setToggleDialogOpen(false);
      setAgentToToggle(null);
    } catch (error: any) {
      toast({ title: "Erro ao alterar status", description: error.message, variant: "destructive" });
    } finally {
      setIsApplyingToggle(false);
    }
  };

  const handleToggleApplyToAll = async () => {
    if (!agentToToggle || !workspaceId) return;
    const scope = buildConversationScope(agentIntegrationLinks, selectedIntegrationIds);
    if (scope.integrationIds.length === 0) {
      toast({
        title: "Nenhuma conversa para alterar",
        description: "Vincule este agente a uma integração em Integrações → Conexão (campo Agente para DMs) para aplicar às conversas.",
        variant: "destructive",
      });
      return;
    }
    setIsApplyingToggle(true);
    try {
      try {
        await supabase.rpc('set_state_change_context', {
          p_member_id: user?.id ?? null,
          p_reason: pendingNewStatus ? 'agent_enabled' : 'agent_disabled',
        });
      } catch {}

      const eligibleIds = await fetchEligibleConversationIds(
        agentToToggle.id,
        pendingNewStatus,
        scope,
      );

      if (pendingNewStatus) {
        const activeBlockedIds = await fetchActiveBlockedConversationIds(
          agentToToggle.id,
          scope,
        );
        if (activeBlockedIds.length > 0) {
          for (let i = 0; i < activeBlockedIds.length; i += 100) {
            const { error: e } = await supabase
              .from("conversations")
              .update({ state: "paused", followup_enabled: false, updated_at: new Date().toISOString() })
              .in("id", activeBlockedIds.slice(i, i + 100));
            if (e) throw e;
          }
        }
      }

      if (eligibleIds.length > 0) {
        for (let i = 0; i < eligibleIds.length; i += 100) {
          const { error: bulkError } = await supabase
            .from("conversations")
            .update(
              pendingNewStatus
                ? { state: "active", updated_at: new Date().toISOString() }
                : { state: "paused", followup_enabled: false, updated_at: new Date().toISOString() },
            )
            .in("id", eligibleIds.slice(i, i + 100));
          if (bulkError) throw bulkError;
        }
      }

      await updateAgentOnly(agentToToggle, pendingNewStatus, {
        skipConversationSideEffects: true,
      });

      const integrationsLabel = agentIntegrationLinks
        .filter((link) => selectedIntegrationIds.includes(link.id))
        .map((link) => link.label)
        .join(", ");

      const blockedSuffix = pendingNewStatus && blockedCount > 0
        ? ` ${blockedCount} lead(s) permaneceram pausados por estarem em estágios com IA desligada (ex.: Atendimento Humano).`
        : "";
      toast({
        title: pendingNewStatus ? "Agente ativado e aplicado às conversas" : "Agente desativado e aplicado às conversas",
        description: pendingNewStatus
          ? `${affectedCount ?? 0} conversa(s) migradas para IA${integrationsLabel ? ` (${integrationsLabel})` : ""}.${blockedSuffix} Conversas encerradas não foram alteradas.`
          : `${affectedCount ?? 0} conversa(s) passaram para atendimento humano${integrationsLabel ? ` (${integrationsLabel})` : ""}. Conversas encerradas não foram alteradas.`,
      });
      setToggleDialogOpen(false);
      setAgentToToggle(null);
    } catch (error: any) {
      toast({ title: "Erro ao aplicar às conversas", description: error.message, variant: "destructive" });
    } finally {
      setIsApplyingToggle(false);
    }
  };

  const toggleIntegrationSelection = (integrationId: string) => {
    setSelectedIntegrationIds(prev =>
      prev.includes(integrationId) ? prev.filter(id => id !== integrationId) : [...prev, integrationId]
    );
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setSelectedAgent(null);
      fetchAgents();
    }
  };

  const handleAgentSaved = async (agentId: string) => {
    const list = await fetchAgents();
    const next = list.find((a) => a.id === agentId);
    if (next) setSelectedAgent(next as Agent);
  };
  return <div className="p-2 sm:p-4 lg:p-6 max-w-5xl mx-auto space-y-4 sm:space-y-5 lg:space-y-6 relative">
      {shouldShowPaywall && <PaywallOverlay featureName="Gerenciar Agentes" minPlan="Inicial" />}
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Agentes de IA</h1>
          <p className="text-muted-foreground mt-1 sm:mt-2 text-xs sm:text-sm lg:text-base">Configure seus agentes de IA </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button onClick={handleCreateAgent} size="sm" className="w-full sm:w-auto h-9 sm:h-10">
            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
            <span className="text-xs sm:text-sm">Novo Agente</span>
          </Button>
          <Button variant="outline" onClick={() => navigate('/agents-test')} size="sm" className="w-full sm:w-auto h-9 sm:h-10">
            <TestTube className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
            <span className="text-xs sm:text-sm">Testar</span>
          </Button>
        </div>
      </div>

      {agents.length === 0 ? <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12 px-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 sm:mb-4">
              <Bot className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">Nenhum agente configurado</h3>
            <p className="text-muted-foreground text-center mb-3 sm:mb-4 max-w-md text-xs sm:text-sm">
              Crie seu primeiro agente de IA para começar a automatizar atendimentos
            </p>
            <Button onClick={handleCreateAgent} size="sm" className="h-9 sm:h-10">
              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
              <span className="text-xs sm:text-sm">Criar Primeiro Agente</span>
            </Button>
          </CardContent>
        </Card> : <Card>
          <CardContent className="p-3 sm:p-4 lg:p-6">
            <Accordion type="multiple" className="w-full">
              {agents.map(agent => <AccordionItem key={agent.id} value={agent.id} className="border-b last:border-0">
                  <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4 min-w-0 gap-2">
                    <div className="flex items-center justify-between min-w-0 flex-1 overflow-hidden pr-2 sm:pr-4">
                      <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 min-w-0 flex-1 overflow-hidden">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 ${agent.is_active ? 'bg-primary/10' : 'bg-muted'}`}>
                          <Bot className={`w-5 h-5 sm:w-6 sm:h-6 ${agent.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="text-left min-w-0 flex-1 overflow-hidden">
                          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                            <h3 className="font-semibold text-sm sm:text-base truncate min-w-0" title={agent.name}>{agent.name}</h3>
                          </div>
                          <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1 flex-wrap">
                            <p className="text-xs sm:text-sm text-muted-foreground">{roleLabels[agent.role] || agent.role}</p>
                            {(agent as any).language && <Badge variant="outline" className="text-[10px] sm:text-xs">
                                {(agent as any).language === 'pt-BR' ? '🇧🇷' : (agent as any).language === 'en-US' ? '🇺🇸' : (agent as any).language === 'es-ES' ? '🇪🇸' : (agent as any).language === 'fr-FR' ? '🇫🇷' : '🌐'}
                              </Badge>}
                          </div>
                        </div>
                      </div>
                      <div 
                        className="flex items-center gap-1.5 flex-shrink-0 ml-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {agent.is_active && (
                          <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                        )}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="p-1.5 rounded-md hover:bg-muted transition-colors"
                                onClick={() => handleDuplicateClick(agent)}
                              >
                                <Copy className="w-4 h-4 text-muted-foreground" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Duplicar agente</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Switch
                          checked={agent.is_active}
                          onCheckedChange={() => handleQuickToggle(agent)}
                          aria-label={agent.is_active ? "Desativar agente" : "Ativar agente"}
                        />
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                    <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-12 lg:pl-16">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
                        {/* Persona */}
                        {(agent as any).display_name && <div className="p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-4 h-4 text-primary" />
                              <p className="text-xs font-medium text-muted-foreground">Persona</p>
                            </div>
                            <p className="text-sm font-medium">{(agent as any).display_name}</p>
                          </div>}

                        {/* Tom de Voz */}
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="w-4 h-4 text-primary" />
                            <p className="text-xs font-medium text-muted-foreground">Tom de Voz</p>
                          </div>
                          <p className="text-sm">{agent.tone}</p>
                        </div>

                        {/* Horário de Funcionamento */}
                        {agent.businessHours && agent.businessHours.length > 0 && <div className="p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock className="w-4 h-4 text-primary" />
                              <p className="text-xs font-medium text-muted-foreground">Horário</p>
                            </div>
                            <p className="text-sm">
                              {agent.businessHours.length === 7 ? <>Todos os dias: {agent.businessHours[0].start_time} - {agent.businessHours[0].end_time}</> : agent.businessHours.length === 5 ? <>Seg-Sex: {agent.businessHours[0].start_time} - {agent.businessHours[0].end_time}</> : <>{agent.businessHours.length} dias configurados</>}
                            </p>
                          </div>}

                        {/* Produtos */}
                        {agent.productSummary && agent.productSummary.count > 0 && <div className="p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Package className="w-4 h-4 text-primary" />
                              <p className="text-xs font-medium text-muted-foreground">Produtos ({agent.productSummary.count})</p>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {agent.productSummary.sampleNames.map((name, idx) => <Badge key={idx} variant="outline" className="text-xs">
                                  {name}
                                </Badge>)}
                              {agent.productSummary.count > 3 && <Badge variant="outline" className="text-xs">
                                  +{agent.productSummary.count - 3}
                                </Badge>}
                            </div>
                          </div>}
                      </div>

                      {/* Mensagem de Saudação */}
                      {(agent as any).greeting_message && <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-xs font-medium text-muted-foreground mb-2">Mensagem de Saudação</p>
                          <p className="text-sm italic text-muted-foreground">"{(agent as any).greeting_message}"</p>
                        </div>}

                      {/* Botões de Ação */}
                      <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <Button onClick={() => handleEditAgent(agent)} size="sm" className="w-full sm:w-auto h-10 bg-primary hover:bg-primary/90 text-primary-foreground border-0 min-w-0 sm:min-w-[140px]">
                          <Edit className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="text-sm">Configurar Agente</span>
                        </Button>
                        <Button variant="destructive" onClick={() => handleDeleteClick(agent)} size="sm" className="w-full sm:w-auto h-10 min-w-0 sm:min-w-[140px]">
                          <Trash2 className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="text-sm">Excluir</span>
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </CardContent>
        </Card>}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="w-[95vw] sm:max-w-md p-4 sm:p-6">
          <AlertDialogHeader className="space-y-4">
            <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500" />
              </div>
              Confirmar Exclusão
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-sm">
                  <span className="text-muted-foreground">Excluir: </span>
                  <span className="font-semibold text-primary">{agentToDelete?.name}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Esta ação irá remover permanentemente:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/70">•</span>
                    O agente e todas as suas configurações
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/70">•</span>
                    Horários de funcionamento e produtos vinculados
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="w-full sm:w-auto border-0 bg-transparent hover:bg-muted">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90">
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent className="w-[95vw] sm:max-w-md p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Copy className="w-5 h-5 text-primary" />
              </div>
              Duplicar Agente
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-sm text-muted-foreground">
                  Todas as configurações, produtos, horários e follow-up serão copiados. O novo agente será criado <strong>desativado</strong>.
                </p>
                <Input
                  value={duplicateName}
                  onChange={(e) => setDuplicateName(e.target.value)}
                  placeholder="Nome do novo agente"
                  className="mt-2"
                  autoFocus
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="w-full sm:w-auto border-0 bg-transparent hover:bg-muted" disabled={isDuplicating}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDuplicateConfirm}
              disabled={isDuplicating || duplicateName.trim().length < 2}
              className="w-full sm:w-auto"
            >
              <Copy className="w-4 h-4 mr-2" />
              {isDuplicating ? "Duplicando..." : "Duplicar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Toggle scope dialog */}
      <Dialog open={toggleDialogOpen} onOpenChange={(open) => {
        setToggleDialogOpen(open);
        if (!open) {
          setAgentToToggle(null);
          setAffectedCount(null);
          setBlockedCount(0);
          setAgentIntegrationLinks([]);
        }
      }}>
        <DialogContent className="w-[95vw] sm:max-w-lg p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${pendingNewStatus ? 'bg-primary/10' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                <Bot className={`w-5 h-5 ${pendingNewStatus ? 'text-primary' : 'text-amber-600 dark:text-amber-500'}`} />
              </div>
              {pendingNewStatus ? "Ativar agente" : "Desativar agente"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2 text-sm text-muted-foreground">
                <p>
                  {pendingNewStatus
                    ? <>Você está prestes a <strong className="text-foreground">ativar</strong> o agente <strong className="text-foreground">{agentToToggle?.name}</strong>. Escolha o escopo:</>
                    : <>Você está prestes a <strong className="text-foreground">desativar</strong> o agente <strong className="text-foreground">{agentToToggle?.name}</strong>. Escolha o escopo:</>}
                </p>
                {hasMultipleActiveAgents && (
                  <p className="text-xs italic">
                    Observação: ao desativar, conversas deste agente podem ser reatribuídas a outro agente ativo do workspace (comportamento do sistema).
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Channel selection (only when applying to all + 2+ channels) */}
            {agentIntegrationLinks.length >= 2 && (
              <div className="space-y-2 rounded-lg border border-border p-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Conexões afetadas ao aplicar a todas as conversas:
                </p>
                <div className="space-y-2">
                  {agentIntegrationLinks.map((link) => (
                    <div key={link.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`int-${link.id}`}
                        checked={selectedIntegrationIds.includes(link.id)}
                        onCheckedChange={() => toggleIntegrationSelection(link.id)}
                      />
                      <Label htmlFor={`int-${link.id}`} className="flex items-center gap-2 cursor-pointer text-sm min-w-0">
                        {link.channel === "whatsapp" && <MessageCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
                        {link.channel === "instagram" && <Instagram className="w-4 h-4 text-pink-600 flex-shrink-0" />}
                        {link.channel === "messenger" && <MessageSquare className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                        <span className="truncate">
                          <span className="text-foreground">{link.label}</span>
                          {link.detail && (
                            <span className="text-muted-foreground"> · {link.detail}</span>
                          )}
                        </span>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {agentIntegrationLinks.length === 1 && (
              <div className="rounded-lg border border-border p-3 text-xs text-muted-foreground">
                Será aplicado às conversas de{" "}
                <strong className="text-foreground">{agentIntegrationLinks[0].label}</strong>
                {agentIntegrationLinks[0].detail && (
                  <> ({agentIntegrationLinks[0].detail})</>
                )}
                .
              </div>
            )}

            {agentIntegrationLinks.length === 0 && (
              <div className="rounded-lg border border-border p-3 text-xs text-muted-foreground">
                Este agente não está vinculado a nenhuma integração ativa. Em{" "}
                <strong className="text-foreground">Integrações → Conexão</strong>, selecione este agente no campo{" "}
                <strong className="text-foreground">Agente para DMs</strong> para poder aplicar às conversas.
              </div>
            )}

            {/* Estimated count */}
            {agentIntegrationLinks.length > 0 && (
              <div className="rounded-lg bg-muted/50 p-3 text-xs">
                {affectedCount === null ? (
                  <span className="text-muted-foreground">Calculando conversas afetadas...</span>
                ) : (
                  <>
                    <span className="font-medium text-foreground">{affectedCount}</span>{" "}
                    <span className="text-muted-foreground">
                      conversa(s) {pendingNewStatus ? "pausadas serão migradas para a IA" : "ativas passarão para humano"}.
                      {" "}Conversas encerradas não são alteradas.
                    </span>
                  </>
                )}
              </div>
            )}

            {pendingNewStatus && blockedCount > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                <p className="font-medium">
                  {blockedCount} lead(s) permanecerão com IA pausada
                </p>
                <p className="mt-1">
                  Esses leads estão em estágios configurados para desligar a IA (ex.: Atendimento Humano). Para reativar a IA nessas conversas, mova o lead para outro estágio no funil primeiro.
                </p>
              </div>
            )}

          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={handleToggleAgentOnly}
              disabled={isApplyingToggle}
              className="w-full sm:w-auto"
            >
              Apenas alterar o agente
            </Button>
            <Button
              onClick={handleToggleApplyToAll}
              disabled={
                isApplyingToggle ||
                agentIntegrationLinks.length === 0 ||
                selectedIntegrationIds.length === 0
              }
              className="w-full sm:w-auto"
            >
              {isApplyingToggle ? "Aplicando..." : "Aplicar a todas as conversas"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AgentDialog open={isDialogOpen} onOpenChange={handleDialogClose} onSaved={handleAgentSaved} agent={selectedAgent} workspaceId={workspaceId} />
    </div>;
}